import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Download, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISSED_KEY = "chronos.pwa.dismissed.v1";
const INSTALLED_KEY = "chronos.pwa.installed.v1";

export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Already installed (standalone) or previously dismissed
    const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches ||
                         window.navigator.standalone === true;
    if (isStandalone) return;
    try {
      if (localStorage.getItem(INSTALLED_KEY) === "1") return;
      const d = localStorage.getItem(DISMISSED_KEY);
      if (d) {
        const dismissedAt = parseInt(d, 10);
        // re-offer after 14 days
        if (Date.now() - dismissedAt < 14 * 24 * 3600 * 1000) return;
      }
    } catch (e) { /* ignore */ }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      // slight delay so we don't fight with the onboarding modal
      setTimeout(() => setVisible(true), 4000);
    };
    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, "1"); } catch (e) {}
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        try { localStorage.setItem(INSTALLED_KEY, "1"); } catch (e) {}
      }
    } catch (e) { /* ignore */ }
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (e) {}
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && deferred && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 25 }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[85] max-w-md w-[calc(100%-2rem)]"
          data-testid="pwa-install-prompt"
        >
          <div className="glass-strong rounded-3xl p-4 sm:p-5 shadow-2xl border-amber-400/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 to-fuchsia-500/10" aria-hidden />
            <button
              onClick={dismiss}
              className="absolute top-2 right-2 text-stone-400 hover:text-stone-100 p-1 z-10"
              data-testid="pwa-dismiss"
              aria-label={t("pwa.dismiss")}
            >
              <X className="w-4 h-4" />
            </button>
            <div className="relative z-10 flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center text-amber-300 flex-shrink-0">
                <Smartphone className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-amber-300 font-bold">{t("pwa.kicker")}</div>
                <div className="mt-1 font-serif text-lg font-bold text-stone-50">{t("pwa.title")}</div>
                <p className="mt-1 text-xs text-stone-300 leading-relaxed">{t("pwa.desc")}</p>
                <div className="mt-3 flex gap-2">
                  <Button
                    onClick={install}
                    className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-4 py-1.5 h-auto text-sm"
                    data-testid="pwa-install"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    {t("pwa.install")}
                  </Button>
                  <button
                    onClick={dismiss}
                    className="text-xs text-stone-400 hover:text-stone-200"
                    data-testid="pwa-later"
                  >
                    {t("pwa.later")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
